/**
 * The page a stranger sees.
 *
 * Two things arrive here: an unknown path, and a hostname that maps to no
 * customer. Both were previously dead ends — an unknown path silently
 * redirected to a sign-in prompt, and an unmapped host showed an internal
 * diagnostic telling the visitor to add a row to portal.CustomerDomains.
 *
 * Neither is right for someone who mistyped a URL or followed a stale link.
 * They are a prospect, and this is the only page of ours they may ever see.
 *
 * Deliberately outside Layout, so it makes no /api/me call and needs no
 * session — it must render on a host the portal knows nothing about.
 *
 * It also says nothing about whether any particular customer exists here.
 * Confirming or denying that to an anonymous visitor would leak the customer
 * list one guess at a time.
 */

import PublicShell from '../components/PublicShell.jsx';

const DEMO_URL = 'https://vizora.com/book-a-free-demo/';
const SITE_URL = 'https://vizora.com/';

const BENEFITS = [
  'One single source of truth for reporting',
  'KPIs your current software cannot produce',
  'Enterprise analytics without the overheads'
];

export default function PublicNotFound({ variant = 'not-found' }) {
  const unknownHost = variant === 'unknown-host';

  return (
    <PublicShell
      heading={unknownHost
        ? 'There is no Vizora portal at this address'
        : 'We could not find that page'}
      lead={unknownHost
        ? 'This web address does not lead to a Vizora customer portal. '
          + 'If your organisation uses Vizora, check the link your '
          + 'administrator gave you.'
        : 'The link may be out of date, or the address may have a typo.'}
    >
      <p className="public-pitch">
        <strong>Your reporting team, without the overheads.</strong> Your
        systems run the business, but their reporting is rigid, incomplete
        and disconnected from finance. Vizora is your outsourced BI team —
        connecting your systems and delivering the KPIs you actually need.
      </p>

      <ul className="public-benefits">
        {BENEFITS.map(text => (
          <li key={text}>
            <i className="bi bi-check-lg" aria-hidden="true" />
            {text}
          </li>
        ))}
      </ul>

      {/* Same teal voice as the landing page's prospect row: whoever reaches
          this page is a stranger, not a customer, and there is no Sign in here
          for a magenta button to compete with. */}
      <div className="public-actions">
        <a className="btn btn-vz-teal" href={DEMO_URL}>Book a free demo</a>
        <a className="btn btn-vz-teal-outline" href={SITE_URL}>
          Explore vizora.com
        </a>
        {/* Only when a portal does exist on this host - on an unmapped host
            this link would send the visitor straight back here. */}
        {!unknownHost && (
          <a className="btn btn-link" href="/">Back to your dashboard</a>
        )}
      </div>
    </PublicShell>
  );
}
